import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { api } from '@/api/client';
import type { Folder, List, Section, ListItem, InheritedCompletion, User } from '@/types';

export interface DataState {
  folders: Folder[];
  lists: List[];
  sections: Section[];
  listItems: ListItem[];
  inheritedCompletions?: InheritedCompletion[];
}

interface OptimisticUpdateOptions {
  update?: DataState | ((prev: DataState) => DataState);
  originalState?: Partial<DataState>;
}

interface DataContextType extends DataState {
  user: User | null;
  userLoading: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  refreshData: () => Promise<void>;
  runOptimisticUpdate: (task: () => Promise<any>, options?: OptimisticUpdateOptions) => Promise<void>;
  setOptimisticData: React.Dispatch<React.SetStateAction<DataState>>;
}

const DataContext = createContext<DataContextType | null>(null);

export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [data, setData] = useState<DataState>({
    folders: [],
    lists: [],
    sections: [],
    listItems: [],
    inheritedCompletions: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState<boolean>(true);

  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.data.getAll();
      setData({
        folders: response.folders || [],
        lists: response.lists || [],
        sections: response.sections || [],
        listItems: response.items || [],
        inheritedCompletions: response.inherited_completions || [],
      });
    } catch (error) {
      console.error('Failed to load all data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadUser = useCallback(async () => {
    setUserLoading(true);
    try {
      const currentUser = await api.auth.me();
      setUser(currentUser);
      await loadAllData();
    } catch (error) {
      setUser(null);
      setData({
        folders: [],
        lists: [],
        sections: [],
        listItems: [],
        inheritedCompletions: [],
      });
    } finally {
      setUserLoading(false);
      setIsLoading(false);
    }
  }, [loadAllData]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const runOptimisticUpdate = async (
    task: () => Promise<any>,
    options?: OptimisticUpdateOptions
  ) => {
    setIsUpdating(true);
    const originalState: DataState = {
      ...data,
      ...(options?.originalState || {}),
    };

    if (options?.update) {
      if (typeof options.update === 'function') {
        setData(options.update);
      } else {
        setData(options.update);
      }
    }

    try {
      await task();
    } catch (error) {
      console.error('An error occurred during update, reverting state:', error);
      setData(originalState);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const value: DataContextType = {
    ...data,
    user,
    userLoading,
    isLoading,
    isUpdating,
    refreshData: loadAllData,
    runOptimisticUpdate,
    setOptimisticData: setData,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
